<script>
    import { onMount } from "svelte";

    import { _getDisplayDate, getEncounterMissingProperties, getHealthFundIcon, getPatientIcon, getSurveyIcon } from "../../../../../Pages/PatientEncounter/helpers/encounterHelper.svelte";

    import { getUnderAndOverPaymentProperties } from "../../../../../Pages/PatientEncounter/helpers/encounterUtils.svelte";

    import { infinityIcon } from "../../../../../Utils/utilConstants.svelte";

    import {
    getFormatDateAndTime,
        getGenderType,
        getPatientAge,
        getPatientAgeString,
        getTime,
        getTitle,
        isObjectEmpty,
    } from "../../../../../Utils/utilFunctions.svelte";

    import CustomTooltip from "../../../../Common/CustomTooltip.svelte";

    import { Loader, RichTextEditor } from "../../../../Common/index.svelte";

    import { apiMethods } from "../../../../../Services/index.svelte";

    export let row = {};
    export let isFeeEstimate = false;
    export let hideInfo = false,
        hideIcons = false,
        hideAlertIcon = false;
    export let containerStyle = "";

    $: displayOptions = [
        {
            key: "patientName",
            content: isObjectEmpty(row) ? "" : _getPatientName(row.patient),
            tooltipContent: isObjectEmpty(row)
                ? ""
                : _getPatientName(row.patient, true),
            style: "text-lg font-bold flex items-start justify-start gap-x-1",
            isTruncated: false,
            hasFetchedPatientDetails: false,
            hasValidContactInfo: false,
            tooltipContactInfo: "",
        },
        {
            key: "patientInfo",
            content: isObjectEmpty(row) ? "" : _getPatientInfo(row),
            tooltipContent: isObjectEmpty(row)
                ? ""
                : _getPatientInfo(row, true),
            style: "text-left text-[12px]",
            isTruncated: false,
        },
        // {
        //     key: "hospitalInfo",
        //     content: isObjectEmpty(row) ? "" : _getHospitalInfo(row.hospital),
        //     tooltipContent: isObjectEmpty(row)
        //         ? ""
        //         : _getHospitalInfo(row.hospital, true),
        //     style: "text-left text-[12px]",
        //     isTruncated: false,
        // },
    ];

    $: isPatientBadDebtor = row.patient?.badDebtor;
    $: isPatientBadDebtorByMedicare = row.patient?.badDebtorByMedicare;
    $: isPatientDeceased = row.patient?.deceased;
    $: patientErrorTooltipText = _getPatientErrorTooltipText(
        isPatientBadDebtor,
        isPatientBadDebtorByMedicare,
        isPatientDeceased,
    );

    $: isPatientAlert = !!row.patient?.alerts;

    $: encounterMissingProperties = getEncounterMissingProperties(row);

    $: encounterUnderOrOverPayment = getUnderAndOverPaymentProperties(row);

    $: visibleTooltipBlocks = [
        isPatientBadDebtor || isPatientBadDebtorByMedicare || isPatientDeceased,
        isPatientAlert,
        encounterMissingProperties.length > 0,
    ];

    $: showTooltip = !!visibleTooltipBlocks.filter(Boolean).length;

    $: containerWidth =
        (!hideInfo || !hideIcons) && !hideAlertIcon
            ? "w-72"
            : (!hideInfo || !hideIcons) && !hideAlertIcon
              ? !hideInfo
                  ? "w-60"
                  : "w-36"
              : "w-12";

    $: infoSectionWidth = !hideInfo ? "w-60" : !hideIcons ? "w-36" : "w-0";

    const truncateMaxWidth = 220;
    const truncateClass = `truncate max-w-[${truncateMaxWidth}px]`;

    let isLoadingPatientDetail = false,
        loadingPatientDetailPromise = null;

    onMount(() => {
        setTimeout(() => {
            checkIfTruncated();
        }, 1000);
    });

    export const checkIfTruncated = () => {
        const hiddenContainer = document.createElement("div");
        hiddenContainer.style.position = "absolute";
        hiddenContainer.style.visibility = "hidden";
        hiddenContainer.style.whiteSpace = "nowrap";
        hiddenContainer.style.pointerEvents = "none";
        document.body.appendChild(hiddenContainer);

        displayOptions = displayOptions.map((option) => {
            hiddenContainer.style.fontSize =
                option.key == "patientName" ? "18px" : "12px";
            hiddenContainer.style.fontWeight =
                option.key == "patientName" ? "700" : "400";

            hiddenContainer.innerHTML = option.content;

            return {
                ...option,
                isTruncated:
                    hiddenContainer.scrollWidth + 10 > truncateMaxWidth,
            };
        });

        document.body.removeChild(hiddenContainer);
    };

    export const resetLoadPatientContactInfo = () => {
        displayOptions = displayOptions.map((option) => {
            if (option.key != "patientName") return option;
            return {
                ...option,
                hasFetchedPatientDetails: false,
            };
        });
    };

    const loadPatientDetails = async () => {
        const patientDetails = displayOptions.find(
            (option) => option.key == "patientName",
        );

        if (!patientDetails || patientDetails.hasFetchedPatientDetails) return;

        if (isLoadingPatientDetail) return loadingPatientDetailPromise;

        isLoadingPatientDetail = true;

        loadingPatientDetailPromise = apiMethods.getMethod
            .getPatientById(row.patient?.id)
            .then((res) => {
                if (res?.id) _syncPatientDetails(res);
            })
            .finally(() => {
                isLoadingPatientDetail = false;
                loadingPatientDetailPromise = null;
            });

        return loadingPatientDetailPromise;
    };

    // HELPER FUNCTIONS
    const _getPatientName = (
        { title, firstName, lastName },
        isTooltipContent = false,
    ) =>
        isTooltipContent
            ? `<div class="text-[12px]">
                ${getTitle(title)} ${firstName} ${lastName}    
            </div>`
            : `<div class="${truncateClass} text-lg">
                ${getTitle(title)} ${firstName} ${lastName}    
            </div>`;

    const _getPatientInfo = (
        { patient, scheduledDate, dischargeDate },
        isTooltipContent = false,
    ) => {
        let patientAge = "",
            patientGender = "",
            encounterStart = "",
            encounterEnd = "";

        patientAge = !patient.birthDate
            ? `<span class="text-[12px] text-[#F41264]">DOB is Missing</span>`
            : `<span class="text-[12px]">Age ${getPatientAgeString(patient.birthDate)}</span>`;

        patientGender = `<span class="text-[12px] pl-1">
                            ${
                                patient.sex == 2
                                    ? "Unknown"
                                    : getGenderType(patient.sex)
                            }
                        </span>`;

        const _getTime = (date) => {
            if (!date) return `<i class="${infinityIcon}"></i>`;

            const dateArr = date.split("T")[0].split("-");
            if (Number(dateArr[0] <= 1900))
                return `<i class="${infinityIcon}"></i>`;
            return `<span>${getTime(date)}</span>`;
        };

        encounterStart = _getTime(scheduledDate);

        encounterEnd = _getTime(dischargeDate);

        return isTooltipContent
            ? `<div class="text-[12px]">
                    ${patientAge} ${patientGender} | ${isFeeEstimate ? "Est." : "Enc."} ${encounterStart} - ${encounterEnd}
                </div>`
            : `<div class="flex items-center overflow-hidden text-ellipsis inline-block text-[12px] ${truncateClass}">
                    ${patientAge} ${patientGender} | ${isFeeEstimate ? "Est." : "Enc."} ${encounterStart} - ${encounterEnd}
                </div>`;
    };

    const _getHospitalInfo = (hospital, isTooltipContent = false) => {
        if (!hospital || isObjectEmpty(hospital))
            return `<div class="text-[12px] text-[#F41264]">
                        Missing Hospital
                    </div>`;

        return `<div class="text-[12px] ${isTooltipContent ? "" : truncateClass}">
                    ${hospital.name}, ${hospital.state}
                </div>`;
    };

    const _getPatientErrorTooltipText = (
        badDebtor,
        badDebtorByMedicare,
        deceased,
    ) => {
        const text = [];

        if (deceased) text.push("Deceased");

        if (badDebtorByMedicare) text.push("Bad Debtor recognised by Medicare Number");
        else if (badDebtor) text.push("Bad Debtor");

        return text.join(", ");
    };

    const _syncPatientDetails = (patient) =>
        (displayOptions = displayOptions.map((option) => {
            if (option.key != "patientName") return option;
            return {
                ...option,
                hasFetchedPatientDetails: true,
                hasValidContactInfo:
                    _getPatientContactInfo(patient).hasValidInfo,
                tooltipContactInfo: _getPatientContactInfo(patient).tooltip,
            };
        }));

    const _getPatientContactInfo = (patient) => {
        const { address, email, patientContacts, phone } = patient;

        const hasValidInfo =
            address ||
            email ||
            phone ||
            (!!patientContacts?.length &&
                patientContacts.some((contact) => contact.useAsContact));

        const defaultContactPerson =
            !!patientContacts?.length &&
            patientContacts.some((contact) => contact.useAsContact)
                ? patientContacts.find((contact) => contact.useAsContact)
                : null;

        const hasValidDefaultContactPersonInfo =
            defaultContactPerson &&
            (defaultContactPerson.name ||
                defaultContactPerson.email ||
                defaultContactPerson.phone);

        const tooltip = !hasValidInfo
            ? "No available contact information"
            : `<div class="flex flex-col flex-1 gap-y-1 items-start text-[12px]">
                <p class="pt-2 font-bold">Patient contact info:</p>
                ${address ? `<p>Address: ${address}</p>` : ""}
                ${email ? `<p>Email: <a href="mailto:${email}" class="underline cursor-pointer hover:text-secondary" onclick="event.stopPropagation()">${email}</a></p>` : ""}
                ${phone ? `<p>Contact No.: <a href="tel:${phone}" class="underline cursor-pointer hover:text-secondary" onclick="event.stopPropagation()">${phone}</a></p>` : ""}
                ${
                    !defaultContactPerson
                        ? ""
                        : `
                            <p class="pt-2 font-bold">Default patient contact info:</p>
                            ${
                                !hasValidDefaultContactPersonInfo
                                    ? "No valid default contact info"
                                    : `
                                    ${defaultContactPerson.name ? `<p>Name: ${defaultContactPerson.name}</p>` : ""}
                                    ${defaultContactPerson.relationship ? `<p>Relationship: ${defaultContactPerson.relationship}</p>` : ""}
                                    ${defaultContactPerson.email ? `<p>Email: <a href="mailto:${defaultContactPerson.email}" class="underline cursor-pointer hover:text-secondary" onclick="event.stopPropagation()">${defaultContactPerson.email}</a></p>` : ""}
                                    ${defaultContactPerson.phone ? `<p>Phone No.: <a href="tel:${defaultContactPerson.phone}" class="underline cursor-pointer hover:text-secondary" onclick="event.stopPropagation()">${defaultContactPerson.phone}</a></p>` : ""}
                                `
                            }
                        `
                }
            </div>`;

        return { hasValidInfo, tooltip };
    };
</script>

<div
    class="flex {containerWidth} flex-col items-start justify-start gap-y-2 {containerStyle}"
>
    <div class="flex w-full items-start justify-start">
        {#if !hideInfo || !hideIcons}
            <div
                class="flex flex-col px-3 {infoSectionWidth} items-start justify-start"
            >
                {#if !hideInfo}
                    <div class="flex flex-col">
                        {#each displayOptions as option}
                            {#if option.content}
                                {#if option.isTruncated}
                                    <CustomTooltip
                                        contentClass="bg-[#fff] border-none"
                                        contentStyle="box-shadow: 4px 4px 13.7px 1px rgba(0, 0, 0, 0.25);"
                                        zIndex="10"
                                        hideTimeOutMs={50}
                                        hoverTriggerFunc={() => {
                                            if (option.key == "patientName")
                                                loadPatientDetails();
                                        }}
                                    >

                                        <p
                                            class={`${option.style} cursor-pointer`}
                                            id={option.key}
                                            style="max-width: {truncateMaxWidth}px;"
                                            slot="tooltipTrigger"
                                        >
                                            {@html option.content}
                                        </p>

                                        <div class="flex flex-col gap-y-2">
                                            <p class="cursor-text text-left">
                                                {@html option.tooltipContent}
                                            </p>


                                            {#if option.key == "patientName" && isLoadingPatientDetail}
                                                <div
                                                    class="flex items-center gap-x-2"
                                                >
                                                    <Loader />
                                                    <p>
                                                        Loading patient contact
                                                        information...
                                                    </p>
                                                </div>
                                            {:else}
                                                <p class="cursor-text text-left text-[12px]">
                                                    Created: {getFormatDateAndTime(row.scheduledDate)} {row.updatedByOCRDate ? "(via OCR)": ""}
                                                </p>
                                                <p
                                                    class="cursor-text text-left text-[12px]"
                                                >
                                                    {#if option.hasValidContactInfo}
                                                        {@html option.tooltipContactInfo}
                                                    {:else}
                                                        No available contact
                                                        information
                                                    {/if}
                                                </p>
                                            {/if}
                                           
                                        </div>
                                    </CustomTooltip>
                                {:else if option.key == "patientName"}
                                    <CustomTooltip
                                        contentClass="bg-[#fff] border-none"
                                        contentStyle="box-shadow: 4px 4px 13.7px 1px rgba(0, 0, 0, 0.25);"
                                        zIndex="10"
                                        hideTimeOutMs={50}
                                        hoverTriggerFunc={() =>
                                            loadPatientDetails()}
                                    >
                                        <p
                                            class={`${option.style} cursor-pointer`}
                                            id={option.key}
                                            style="max-width: {truncateMaxWidth}px;"
                                            slot="tooltipTrigger"
                                        >
                                            {@html option.content}
                                        </p>

                                        {#if option.key == "patientName" && isLoadingPatientDetail}
                                            <div
                                                class="flex items-center gap-x-2"
                                            >
                                                <Loader />
                                                <p>
                                                    Loading patient contact information...
                                                </p>
                                            </div>
                                        {:else}
                                            <p class="cursor-text text-left">
                                                {@html option.tooltipContent}
                                            </p>
                                            <p class="cursor-text text-left text-[12px]">
                                                Created: {_getDisplayDate(row.createdDate)} {row.updatedByOCRDate ? "(via OCR)": ""}
                                            </p>
                                            <p class="cursor-text text-left text-[12px]">
                                                {#if option.hasValidContactInfo}
                                                    {@html option.tooltipContactInfo}
                                                {:else}
                                                    No available contact information
                                                {/if}
                                            </p>
                                        {/if}
                                    </CustomTooltip>
                                {:else}
                                    <p
                                        class={option.style}
                                        id={option.key}
                                        style="max-width: {truncateMaxWidth}px;"
                                    >
                                        {@html option.content}
                                    </p>
                                {/if}
                            {/if}
                        {/each}
                        
                    </div>
                {/if}

                {#if !hideIcons}
                    <div class="flex w-60 items-end justify-between">
                        {#if isFeeEstimate}
                            <div />
                        {:else}
                            <div class="flex items-center gap-x-2 w-fit px-2">
                                <CustomTooltip
                                    contentClass="bg-[#fff] border-none"
                                    contentStyle="box-shadow: 4px 4px 13.7px 1px rgba(0, 0, 0, 0.25);"
                                >
                                    <p slot="tooltipTrigger">
                                        {@html getPatientIcon(row, encounterUnderOrOverPayment).icon}
                                    </p>

                                    <p style="color: {getPatientIcon(row, encounterUnderOrOverPayment).patientTooltipColor}">
                                        {@html getPatientIcon(row, encounterUnderOrOverPayment).patientIconTooltip}
                                    </p>
                                </CustomTooltip>

                                <CustomTooltip
                                    contentClass="bg-[#fff] border-none"
                                    contentStyle="box-shadow: 4px 4px 13.7px 1px rgba(0, 0, 0, 0.25);"
                                >
                                    <p slot="tooltipTrigger">
                                        {@html getHealthFundIcon(row, encounterUnderOrOverPayment).icon}
                                    </p>

                                    <p style="color: {getHealthFundIcon(row, encounterUnderOrOverPayment).healthFundTooltipColor}">
                                        {@html getHealthFundIcon(row, encounterUnderOrOverPayment).healthFundIconTooltip}
                                    </p>
                                </CustomTooltip>

                                <CustomTooltip
                                    contentClass="bg-[#fff] border-none"
                                    contentStyle="box-shadow: 4px 4px 13.7px 1px rgba(0, 0, 0, 0.25);"
                                >
                                    <p slot="tooltipTrigger">
                                        {@html getSurveyIcon(row).icon ?? ""}
                                    </p>

                                    <p style="color: {getSurveyIcon(row).surveyTooltipColor}">
                                        {@html getSurveyIcon(row).surveyIconTooltip}
                                    </p>
                                </CustomTooltip>
                            </div>
                        {/if}
                    </div>
                {/if}
            </div>
        {/if}

        {#if showTooltip && !hideAlertIcon}
            <CustomTooltip
                contentClass="bg-[#fff] border-none"
                contentStyle="box-shadow: 4px 4px 13.7px 1px rgba(0, 0, 0, 0.25);"
                hideTimeOutMs={50}
            >
                <img
                    src="/icons/redInfo.svg"
                    alt="Info Icon"
                    class="pb-1 w-[40px] h-[40px] shrink-0"
                    slot="tooltipTrigger"
                />
                <div class="flex flex-col">
                    <!-- Bad Debtor / Deceased -->
                    {#if visibleTooltipBlocks[0]}
                        <div class="flex items-start gap-x-2">
                            <img
                                src="/icons/redInfo_orange.svg"
                                alt="Info Icon"
                                class="w-4 h-4"
                            />
                            <p class="text-[#414D55] text-[12px] font-normal">
                                {patientErrorTooltipText}
                            </p>
                        </div>

                        {#if visibleTooltipBlocks.slice(1).some(Boolean)}
                            <div class="divider" />
                        {/if}
                    {/if}

                    <!-- Patient Alerts -->
                    {#if visibleTooltipBlocks[1]}
                        <div class="flex items-start gap-x-2">
                            <img
                                src="/icons/alertIcon.svg"
                                alt="Info Icon"
                                class="w-4 h-4"
                            />
                            <!-- <p class="text-[12px] font-normarl">
                                                {row.patient.alerts}
                                            </p> -->
                            <div class="flex flex-col">
                                <p
                                    class="text-[12px] text-left text-[#414D55] font-normal"
                                >
                                    Alerts
                                </p>
                                <RichTextEditor
                                    content={row.patient.alerts}
                                    config={{
                                        isDisabled: true,
                                        style: {
                                            border: "none",
                                            minWidth: "100px",
                                            minHeight: 0,
                                        },
                                    }}
                                />
                            </div>
                        </div>

                        {#if visibleTooltipBlocks.slice(2).some(Boolean)}
                            <div class="divider" />
                        {/if}
                    {/if}

                    <!-- Missing encounter details -->
                    {#if visibleTooltipBlocks[2]}
                        <div class="flex items-start gap-x-2">
                            <img
                                src="/icons/alertIcon.svg"
                                alt="Info Icon"
                                class="w-4 h-4"
                            />
                            <div class="flex flex-col items-start gap-y-1">
                                {#each encounterMissingProperties as missingText}
                                    <p
                                        class="text-[#414D55] text-[12px] font-normal"
                                    >
                                        {missingText}
                                    </p>
                                {/each}
                            </div>
                        </div>

                        {#if visibleTooltipBlocks.slice(3).some(Boolean)}
                            <div class="divider" />
                        {/if}
                    {/if}
                </div>
            </CustomTooltip>
        {/if}
    </div>
</div>
